import {
  TB2CRequestAddressData,
  IB2CAddressesResponse,
} from '../../models/b2c/addresses';
import { b2bAddressesApi } from '../b2b/addresses';
import { b2cAddressesApi } from '../b2c/addresses';
import {
  IAddressesCommonData,
  IMappedAddress,
} from '../../models/common/addresses';
import {
  B2B_ADDRESSES_PATH,
  B2C_ADDRESSES_PATH,
  ZIP_CODE_PATH,
} from '../../constants/links';
import {
  IB2BAddressesResponse,
  TB2BRequestAddressData,
} from '../../models/b2b/addresses';
import { apiB2B, apiB2C, apiBase } from '../axios-client';
import { mapAddressListToCommonModel } from '../../utils/mapping-addresses';
import { TCity } from '../../models/common/city-state';
import { AddressSortByValue } from '../../constants/sort';
import { ERROR_CODES } from '../../constants/custom-errors';
import { ADDRESSES_PAGE_SIZE } from '../../constants/general';
import { withUserContext, withMemberId } from '../utils';

const handleAddressesResponse = (
  data: Partial<IB2BAddressesResponse> | Partial<IB2CAddressesResponse>,
) => {
  const addresses = data.addresses?.address;
  const paginationInfo = data.paginationInfo;
  const totalNumberOfAddresses = paginationInfo?.totalNumberOfRecords;

  if (Array.isArray(addresses) && addresses.length > 0) {
    return mapAddressListToCommonModel(addresses, paginationInfo);
  }

  if (!Number(totalNumberOfAddresses)) {
    throw new Error(ERROR_CODES.NO_SAVED_ADR_CODE);
  }

  throw new Error(ERROR_CODES.NO_FOUND_ADR_CODE);
};

export interface ISortConfig {
  page?: number;
  pageSize?: number;
  sortBy?: AddressSortByValue;
  ascendingOrder?: boolean;
}

const getB2BAddresses = withUserContext(
  (
    userId,
    svocId,
    { page, sortBy, ascendingOrder }: ISortConfig,
  ): Promise<IAddressesCommonData> =>
    apiB2B
      .get<IB2BAddressesResponse>(B2B_ADDRESSES_PATH(userId, svocId), {
        params: {
          ps: ADDRESSES_PAGE_SIZE,
          pn: page || 1,
          sb: sortBy || 'lastedited',
          asc: ascendingOrder || false,
        },
      })
      .then(({ data }) => handleAddressesResponse(data)),
);

const getB2CAddresses = withMemberId(
  (memberId, { page, pageSize }: ISortConfig): Promise<IAddressesCommonData> =>
    apiB2C
      .get<IB2CAddressesResponse>(B2C_ADDRESSES_PATH(memberId), {
        params: {
          pageSize:
            typeof pageSize === 'number' ? pageSize : ADDRESSES_PAGE_SIZE,
          pageNumber: typeof page === 'number' ? page : 1,
        },
      })
      .then(({ data }) => handleAddressesResponse(data)),
);

export const addressesApi = {
  getAddresses: (
    isB2B: boolean,
    sortConfig: ISortConfig,
  ): Promise<IAddressesCommonData> => {
    if (isB2B) {
      return getB2BAddresses(sortConfig);
    }
    return getB2CAddresses(sortConfig);
  },
  deleteAddress: (
    isB2B: boolean,
    { addrIdentifier, userId, lastModifiedDate }: IMappedAddress,
  ) => {
    if (isB2B) {
      return b2bAddressesApi.deleteAddress({ addrIdentifier, userId });
    }

    return b2cAddressesApi.deleteAddress({ addrIdentifier, lastModifiedDate });
  },
  changeDefaultAddress: (
    isB2B: boolean,
    newDefaultRequestData: TB2CRequestAddressData | TB2BRequestAddressData,
    oldDefaultRequestData: TB2CRequestAddressData | null,
  ) => {
    if (isB2B) {
      return b2bAddressesApi.editAddress(newDefaultRequestData).then((data) => {
        const newDefaultAdr = data.addresses.address.find(
          (adr) => adr.isDefault,
        );
        if (!newDefaultAdr) {
          throw new Error('Default address not set');
        }
        return newDefaultAdr;
      });
    }

    return b2cAddressesApi
      .editAddress(
        ...[newDefaultRequestData, oldDefaultRequestData].filter(
          (value): value is TB2CRequestAddressData | TB2BRequestAddressData =>
            Boolean(value),
        ),
      )
      .then((data) => {
        const newDefaultAdr = data.addresses.address.find(
          (adr) => adr.isDefault,
        );
        if (!newDefaultAdr) {
          throw new Error('Default address not set');
        }
        return newDefaultAdr;
      });
  },
  getPostalList: (code: string): Promise<Array<TCity>> =>
    apiBase.get(`${ZIP_CODE_PATH}/${code}`).then(({ data }) => {
      if (data && data.postalDetailsList?.length > 0) {
        return data.postalDetailsList;
      }

      throw new Error('postalDetailsList is empty');
    }),
  addNewAddress: (
    isB2B: boolean,
    requestData: TB2CRequestAddressData | TB2BRequestAddressData,
  ) => {
    if (isB2B) {
      return b2bAddressesApi.addNewAddress(
        requestData as TB2BRequestAddressData,
      );
    }
    return b2cAddressesApi.addNewAddress(requestData as TB2CRequestAddressData);
  },
  editAddress: (
    isB2B: boolean,
    requestData: TB2CRequestAddressData | TB2BRequestAddressData,
  ) => {
    if (isB2B) {
      return b2bAddressesApi.editAddress(requestData as TB2BRequestAddressData);
    }
    return b2cAddressesApi.editAddress(requestData as TB2CRequestAddressData);
  },
};
